import React, { useEffect, useState } from 'react';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import axios from 'axios';
import { useLanguage } from '../../../i18n/LanguageContext';
import apiUrl from '../../../config/api';
import {
  getAutomaticKeywordHint,
  getQuestionKeywordTerms,
  getSavedKeywordHint,
  renderHighlightedText,
} from './questionKeywordHighlight';

const QuestionPreviewModal = ({
  show,
  question,
  subjectName,
  isListMode = false,
  currentIndex = 0,
  totalQuestions = 0,
  onHide,
  onPrevious,
  onNext,
  onShowPassage,
  onQuestionUpdated,
}) => {
  const { t } = useLanguage();
  const text = t.subjectsPage;
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keywordMessage, setKeywordMessage] = useState('');
  const [isSavingKeyword, setIsSavingKeyword] = useState(false);
  const [showKeywordPanel, setShowKeywordPanel] = useState(false);

  useEffect(() => {
    if (!question) {
      setKeywordDraft('');
      setKeywordMessage('');
      setShowKeywordPanel(false);
      return;
    }

    setKeywordDraft(getSavedKeywordHint(question) || getAutomaticKeywordHint(question, text));
    setKeywordMessage('');
    setShowKeywordPanel(false);
  }, [question, text]);

  const safeTotalQuestions = Math.max(0, Number(totalQuestions || 0));
  const currentPosition = Math.min(safeTotalQuestions || 1, Math.max(1, Number(currentIndex || 0) + 1));
  const canGoPrevious = isListMode && currentPosition > 1;
  const canGoNext = isListMode && currentPosition < safeTotalQuestions;

  useEffect(() => {
    if (!show || !isListMode) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      const targetTagName = String(event.target?.tagName || '').toUpperCase();

      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(targetTagName)) {
        return;
      }

      if (event.key === 'ArrowLeft' && canGoPrevious) {
        event.preventDefault();
        onPrevious?.();
      }

      if (event.key === 'ArrowRight' && canGoNext) {
        event.preventDefault();
        onNext?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, isListMode, canGoPrevious, canGoNext, onPrevious, onNext]);

  if (!question) {
    return null;
  }

  const isOpenQuestion = question.questionType === 'open';
  const rawAnswers = Array.isArray(question.answers) ? question.answers : [];
  const visibleAnswers = isOpenQuestion
    ? rawAnswers.filter((answer) => String(answer?.text || '').trim())
    : rawAnswers;
  const answerInputType = question.answerType === 'multiple' ? 'checkbox' : 'radio';
  const questionTypeLabel = isOpenQuestion
    ? (text.openQuestion || text.open)
    : (text.closedQuestion || text.closed);
  const keywordPreviewQuestion = {
    ...question,
    keywordHint: keywordDraft,
    keywordHintDisabled: !String(keywordDraft || '').trim(),
  };
  const keywordTerms = getQuestionKeywordTerms(keywordPreviewQuestion, text);

  const handlePassageClick = (event) => {
    onHide?.();
    onShowPassage?.(question._id, event);
  };

  const handleRegenerateKeyword = () => {
    const nextKeywordHint = getAutomaticKeywordHint(question, text);

    setKeywordDraft(nextKeywordHint);
    setKeywordMessage(nextKeywordHint ? '' : text.noKeywordFound);
  };

  const handleSaveKeyword = async () => {
    try {
      setIsSavingKeyword(true);
      setKeywordMessage('');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.put(
        `${apiUrl}/questionKeyword`,
        {
          questionId: question._id,
          keywordHint: keywordDraft,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const updatedQuestion = {
        ...question,
        keywordHint: response.data.keywordHint || '',
        keywordHintDisabled: Boolean(response.data.keywordHintDisabled),
      };

      onQuestionUpdated?.(updatedQuestion);
      setKeywordDraft(updatedQuestion.keywordHint);
      setKeywordMessage(text.keywordSaved);
    } catch (error) {
      console.error('Errore durante il salvataggio della parola chiave:', error);
      setKeywordMessage(text.keywordSaveError);
    } finally {
      setIsSavingKeyword(false);
    }
  };

  return (
    <Modal
      className="modal-theme-bg quiz-review-mode quiz-review-preview-modal"
      show={show}
      onHide={onHide}
      centered
      scrollable
      size="xl"
    >
      <Modal.Header closeButton className="quiz-review-preview-header">
        <div className="quiz-review-preview-title">
          <Modal.Title as="h3" className="mb-0">
            {isListMode ? text.questionListPreviewTitle : text.questionPreviewTitle}
          </Modal.Title>
          {subjectName && <small className="text-muted d-block">{subjectName}</small>}
          {isListMode && safeTotalQuestions > 0 && (
            <small className="text-muted d-block">
              {text.questionProgress(currentPosition, safeTotalQuestions)}
            </small>
          )}
        </div>
        <Badge
          className="quiz-review-preview-type-badge"
          bg={isOpenQuestion ? 'warning' : 'primary'}
          text={isOpenQuestion ? 'dark' : undefined}
        >
          {questionTypeLabel}
        </Badge>
      </Modal.Header>

      <Modal.Body>
        <div className="quiz-screen quiz-review-preview">
          <div className="quiz-review-preview-shell bg-soft-black rounded-3">
            <div className="quiz-review-preview-question-card bg-dark rounded-3">
              <div className="quiz-review-preview-question-heading">
                <h3 className="quiz-review-preview-question-text mb-0">{question.questionText}</h3>
                {!isOpenQuestion && (
                  <Button
                    type="button"
                    variant={showKeywordPanel ? 'primary' : 'soft-info'}
                    size="sm"
                    className="quiz-keyword-toggle quiz-review-preview-keyword-toggle"
                    title={text.findKeyword}
                    onClick={() => setShowKeywordPanel((isVisible) => !isVisible)}
                  >
                    💡
                  </Button>
                )}
              </div>
              {question.passageNumber && (
                <Button
                  type="button"
                  variant="soft-info"
                  size="sm"
                  className="mt-3"
                  onClick={handlePassageClick}
                >
                  {text.readPassage(question.passageNumber)}
                </Button>
              )}
            </div>

            {!isOpenQuestion && showKeywordPanel && (
              <div className="quiz-review-box quiz-review-preview-keyword-card text-start">
                <Form.Group>
                  <Form.Label className="mb-2">💡 {text.keywordLabel}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={keywordDraft}
                    onChange={(event) => {
                      setKeywordDraft(event.target.value);
                      setKeywordMessage('');
                    }}
                    placeholder={text.keywordPlaceholder}
                  />
                </Form.Group>
                <div className="quiz-review-preview-keyword-actions">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleSaveKeyword}
                    disabled={isSavingKeyword}
                  >
                    {isSavingKeyword ? text.saving : text.saveKeyword}
                  </Button>
                  <Button
                    type="button"
                    variant="soft-info"
                    size="sm"
                    onClick={handleRegenerateKeyword}
                  >
                    {text.regenerateKeyword}
                  </Button>
                  {keywordMessage && <small className="text-muted">{keywordMessage}</small>}
                </div>
              </div>
            )}

            {isOpenQuestion ? (
              <div className="quiz-review-box quiz-review-preview-open-answer text-start">
                <Badge bg="warning" text="dark" className="mb-3">{text.manualReviewBadge}</Badge>
                <div className="fw-semibold mb-2">{text.referenceAnswer}</div>
                {visibleAnswers.length ? visibleAnswers.map((answer, index) => (
                  <p key={`${question._id}-open-${index}`} className={index === visibleAnswers.length - 1 ? 'mb-0' : 'mb-3'}>
                    {renderHighlightedText(answer.text || text.emptyQuestionAnswer, keywordTerms)}
                  </p>
                )) : (
                  <p className="mb-0 text-muted">{text.noReferenceAnswer}</p>
                )}
              </div>
            ) : (
              <div className="quiz-review-preview-options">
                {visibleAnswers.length ? visibleAnswers.map((answer, index) => {
                  const answerId = `question-preview-${question._id}-${index}`;

                  return (
                    <div
                      key={answerId}
                      className={`quiz-review-preview-option bg-dark rounded-3 ${answer.isCorrect ? 'is-correct' : ''}`}
                    >
                      <Form.Check className="form-check-inline quiz-review-preview-check">
                        <Form.Check.Input
                          type={answerInputType}
                          id={answerId}
                          checked={Boolean(answer.isCorrect)}
                          readOnly
                        />
                        <Form.Check.Label htmlFor={answerId} className="pl-2">
                          {renderHighlightedText(answer.text || text.emptyQuestionAnswer, keywordTerms)}
                        </Form.Check.Label>
                      </Form.Check>
                    </div>
                  );
                }) : (
                  <div className="quiz-review-preview-empty bg-dark rounded-3">{text.noReferenceAnswer}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="quiz-review-preview-footer">
        {isListMode && (
          <div className="quiz-review-preview-navigation" aria-label={text.questionListPreviewTitle}>
            <Button
              type="button"
              variant="soft-info"
              size="sm"
              onClick={onPrevious}
              disabled={!canGoPrevious}
            >
              {text.previous}
            </Button>
            <span className="quiz-review-preview-counter">
              {text.questionProgress(currentPosition, safeTotalQuestions)}
            </span>
            <Button
              type="button"
              variant="soft-info"
              size="sm"
              onClick={onNext}
              disabled={!canGoNext}
            >
              {text.next}
            </Button>
          </div>
        )}
        <Button className="quiz-review-preview-close" variant="soft-danger" onClick={onHide}>{text.close}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionPreviewModal;
